import { useState, useEffect, useCallback } from "react";
import { ShieldCheck, CreditCard, FileText, Landmark } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// License — READ-ONLY mirror of the Hub's canonical fitter status (WP3).
// Billing truth lives in Henry-Griffitts' internal Hub (Stax card-on-file +
// the signed agreement's fee choice). This page displays it; it never edits it.
// The old Switch to Annual/Monthly buttons are gone on purpose.

export function LicenseFeeView({ user }) {
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [licenseRes, paymentsRes] = await Promise.all([
        fetch(`${API_URL}/api/license`, { credentials: "include" }),
        fetch(`${API_URL}/api/payments/history`, { credentials: "include" }),
      ]);
      if (licenseRes.ok) setData(await licenseRes.json());
      if (paymentsRes.ok) {
        const d = await paymentsRes.json();
        setPayments(d.payments || []);
      }
    } catch (err) {
      console.error("Failed to load license info:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");
  const money = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

  if (loading) {
    return (
      <div className="content" data-testid="license-fees">
        <div className="section-title">License</div>
        <div className="loading-state">Loading license information…</div>
      </div>
    );
  }

  const hub = data?.hub;
  const found = hub?.found;
  const agreement = hub?.agreement;
  const billing = hub?.billing;
  const feeLabel = agreement?.fee_choice === "annual"
    ? "Annual license"
    : agreement?.fee_choice === "monthly"
      ? `${money(57.5)}/month license`
      : "License";

  const statusLabel = !data
    ? "Unavailable"
    : data.source === "hub-unreachable"
      ? "Temporarily unavailable"
      : found
        ? (hub.active ? "Active" : (hub.lifecycle_status || "In progress"))
        : "Being set up";
  const statusColor = found && hub.active ? "var(--hg-green)" : "var(--gold-accent)";

  return (
    <div className="content license-page" data-testid="license-fees">
      <div className="section-title">License</div>
      <p className="section-sub">
        Your Henry-Griffitts fitter license — managed for you by Henry-Griffitts.
      </p>

      {/* Canonical status, mirrored from the Hub */}
      <div className="admin-card license-status-card">
        <div className="license-header">
          <h3><ShieldCheck size={17} style={{ verticalAlign: "-3px", color: "var(--hg-green)", marginRight: 8 }} />License Status</h3>
          <span
            className="status-badge"
            data-testid="license-status-badge"
            style={{ background: "color-mix(in srgb, " + statusColor + " 14%, transparent)", color: statusColor }}
          >
            {statusLabel}
          </span>
        </div>

        <div className="license-details">
          <div className="detail-row">
            <span className="detail-label">Licensed to</span>
            <span className="detail-value">{hub?.name || user?.name || "—"}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Plan</span>
            <span className="detail-value">{feeLabel}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Agreement</span>
            <span className="detail-value">
              {agreement
                ? `${agreement.reference || "Signed"} · ${agreement.status || ""}${agreement.signed_at ? ` · signed ${fmtDate(agreement.signed_at)}` : ""}`
                : "—"}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Payment method</span>
            <span className="detail-value">
              {billing?.card_on_file
                ? `${billing.card_type || "Card"} ending ${billing.card_last_four || "····"} (on file)`
                : "Being set up"}
            </span>
          </div>
          {hub?.activated_at && (
            <div className="detail-row">
              <span className="detail-label">Active since</span>
              <span className="detail-value">{fmtDate(hub.activated_at)}</span>
            </div>
          )}
        </div>

        <p className="license-managed-note" data-testid="license-managed-note">
          <Landmark size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
          Billing and plan changes are handled by Henry-Griffitts — to change anything,
          just reach out and we'll take care of it.
        </p>
      </div>

      {/* Payment history (local records, display only) */}
      <div className="admin-card">
        <h3><CreditCard size={16} style={{ verticalAlign: "-3px", color: "var(--hg-green)", marginRight: 8 }} />Payment History</h3>
        {payments.length === 0 ? (
          <div className="empty-state-hg">
            <FileText size={26} />
            <p>No payments recorded here yet — statements live with Henry-Griffitts.</p>
          </div>
        ) : (
          <div className="payment-history">
            <table className="payments-table">
              <thead>
                <tr><th>Date</th><th>Type</th><th>Amount</th><th>Method</th><th>Status</th></tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.payment_id} data-testid={`payment-${p.payment_id}`}>
                    <td>{fmtDate(p.created_at)}</td>
                    <td>{(p.payment_type || "").replace(/_/g, " ")}</td>
                    <td>{money(p.amount)}</td>
                    <td>{p.payment_method}</td>
                    <td>{p.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contact */}
      <div className="admin-card help-card">
        <h3>Questions about your license or billing?</h3>
        <p>
          Email <a href="mailto:hgsupport@henry-griffitts.com">hgsupport@henry-griffitts.com</a>{" "}
          or call the shop at <a href="tel:+12086641700">(208) 664-1700</a> — a real person answers.
        </p>
      </div>
    </div>
  );
}

export default LicenseFeeView;
